import { useState, useEffect } from "react";
import { m, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useHomepageArticles } from "@/hooks/useArticles";
import { Skeleton } from "@/components/ui/skeleton";
import { getOptimizedImageUrl, generateSrcSet } from "@/lib/imageUtils";

// Inject the hero preload link synchronously during module evaluation of the
// hero render — this lets the browser start fetching the LCP image as soon
// as data is available, instead of waiting for React's commit phase.
const injectedHeroPreload = new Set<string>();
const injectHeroPreload = (src: string) => {
  if (typeof document === "undefined" || injectedHeroPreload.has(src)) return;
  injectedHeroPreload.add(src);
  // Match the smallest variant — mobile is the most common LCP target and
  // desktop will upgrade via srcset without blocking the initial paint.
  const url = getOptimizedImageUrl(src, 480);
  const srcset = generateSrcSet(src, [480, 768, 1024]);
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.dataset.hero = "1";
  if (srcset) {
    link.setAttribute("imagesrcset", srcset);
    link.setAttribute("imagesizes", "100vw");
  }
  link.setAttribute("fetchpriority", "high");
  document.head.appendChild(link);
};

const HeroSection = () => {
  const { data: allArticles, isLoading } = useHomepageArticles();
  const slides = allArticles?.slice(0, 5) ?? [];
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  // Synchronous preload during render — runs before commit/paint
  if (slides[0]?.featured_image_url) {
    injectHeroPreload(slides[0].featured_image_url);
  }



  useEffect(() => {
    if (paused || slides.length === 0) return;
    const timer = setInterval(() => setCurrent((p) => (p + 1) % slides.length), 6000);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  const go = (dir: number) => setCurrent((p) => (p + dir + slides.length) % slides.length);

  if (isLoading) {
    return (
      <section className="relative h-[70vh] min-h-[480px] max-h-[700px] bg-primary flex items-end">
        <div className="container pb-12 md:pb-16 space-y-4">
          <Skeleton className="h-6 w-32 bg-primary-foreground/10" />
          <Skeleton className="h-12 w-3/4 bg-primary-foreground/10" />
          <Skeleton className="h-4 w-1/2 bg-primary-foreground/10" />
        </div>
      </section>
    );
  }

  if (slides.length === 0) return null;

  const slide = slides[current];
  const formattedDate = slide.published_at
    ? new Date(slide.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "";

  return (
    <section
      className="relative h-[70vh] min-h-[480px] max-h-[700px] overflow-hidden bg-primary"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence mode="wait" initial={false}>
        <m.div
          key={current}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="absolute inset-0"
        >
          {slide.featured_image_url ? (
            <img
              src={getOptimizedImageUrl(slide.featured_image_url, 480)}
              srcSet={generateSrcSet(slide.featured_image_url, [480, 768, 1024])}
              sizes="100vw"
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover"
              width={1024}
              height={512}
              fetchPriority="high"
              decoding="async"
            />
          ) : (
            <div className="absolute inset-0 bg-primary" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
        </m.div>
      </AnimatePresence>

      <div className="relative h-full container flex items-end pb-12 md:pb-16 z-10">
        <AnimatePresence mode="wait" initial={false}>
          <m.div
            key={current}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="max-w-2xl"
          >
            {slide.category_name && (
              <span className="category-badge mb-4">{slide.category_name}</span>
            )}
            <h1 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
              {slide.title}
            </h1>
            {slide.excerpt && (
              <p className="text-white/85 font-body text-sm md:text-base leading-relaxed mb-5 max-w-xl">
                {slide.excerpt}
              </p>
            )}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/80 font-semibold">
                <Clock className="w-3.5 h-3.5" />
                <span>{slide.author_name || "Admin"}</span>
                <span className="w-1 h-1 rounded-full bg-secondary" />
                <span>{formattedDate}</span>
              </div>
              <a
                href={`/article/${slide.slug}`}
                className="ml-2 px-5 py-2.5 bg-secondary text-secondary-foreground font-semibold text-xs uppercase tracking-wide hover:brightness-110 transition-all rounded-sm"
                aria-label={`Read full story: ${slide.title}`}
              >
                Read full story
              </a>
            </div>
          </m.div>
        </AnimatePresence>
      </div>

      <button
        onClick={() => go(-1)}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => go(1)}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className="group flex h-3 w-8 items-center"
            aria-label={`Go to slide ${i + 1}`}
          >
            <span
              className={`h-1 w-full origin-center rounded-full transition-[transform,background-color] duration-300 ${
                i === current ? "scale-x-100 bg-secondary" : "scale-x-50 bg-white/40 group-hover:bg-white/60"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
